// @spec GHG-STR-006
/**
 * TanStack Query hooks for GHG settings
 */

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  GHGSettingsResponse,
  Scope1CoverageKey,
  Scope2CoverageKey,
  Scope3CategoryKey,
  UpdateGHGSettingsRequest,
  UpdateScope3CategoryConfig,
} from '../types/ghg-settings';

import {
  fetchGHGSettings,
  fetchSetupWizardState,
  saveSetupWizardState,
  updateGHGSettings,
} from './ghg-settings';
import { useGHGOrganizationId } from './use-ghg-organization-id';

/**
 * Query keys for GHG settings queries
 */
export const ghgSettingsKeys = {
  all: ['ghg-settings'] as const,
  detail: (orgId: string) => [...ghgSettingsKeys.all, 'detail', orgId] as const,
};

/**
 * Hook return type for GHG settings query
 */
export interface UseGHGSettingsReturn {
  settings: GHGSettingsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

/**
 * Hook to fetch GHG settings for the current organization.
 *
 * @example
 * const { settings, isLoading, isError } = useGHGSettings();
 */
export function useGHGSettings(): UseGHGSettingsReturn {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  const query = useQuery({
    queryKey: ghgSettingsKeys.detail(organizationId ?? ''),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchGHGSettings(getToken, organizationId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId,
    staleTime: 60 * 1000, // 1 minute
  });

  return {
    settings: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to update GHG settings with optimistic updates.
 *
 * @example
 * const { mutate, isPending } = useUpdateGHGSettings();
 * mutate({ scope3Categories: { businessTravel: { enabled: false } } });
 */
export function useUpdateGHGSettings() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useGHGOrganizationId();

  return useMutation<
    GHGSettingsResponse,
    Error,
    UpdateGHGSettingsRequest,
    { previous: GHGSettingsResponse | undefined }
  >({
    mutationFn: async (data) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return updateGHGSettings(getToken, organizationId, data);
    },
    onMutate: async (newData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ghgSettingsKeys.detail(organizationId ?? ''),
      });

      // Snapshot previous value
      const previous = queryClient.getQueryData<GHGSettingsResponse>(
        ghgSettingsKeys.detail(organizationId ?? '')
      );

      // Optimistically update scope1 coverage
      if (previous && newData.scope1Coverage) {
        const updated = { ...previous.scope1Coverage };
        for (const [key, value] of Object.entries(newData.scope1Coverage)) {
          if (value !== undefined) {
            const k = key as Scope1CoverageKey;
            updated[k] = { enabled: value.enabled ?? updated[k].enabled };
          }
        }
        queryClient.setQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? ''),
          { ...previous, scope1Coverage: updated }
        );
      }

      // Optimistically update scope2 coverage
      if (previous && newData.scope2Coverage) {
        const current = queryClient.getQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? '')
        ) ?? previous;
        const updated = { ...current.scope2Coverage };
        for (const [key, value] of Object.entries(newData.scope2Coverage)) {
          if (value !== undefined) {
            const k = key as Scope2CoverageKey;
            updated[k] = { enabled: value.enabled ?? updated[k].enabled };
          }
        }
        queryClient.setQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? ''),
          { ...current, scope2Coverage: updated }
        );
      }

      // Optimistically update scope1 notes
      if (previous && newData.scope1Notes) {
        const current = queryClient.getQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? '')
        ) ?? previous;
        queryClient.setQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? ''),
          { ...current, scope1Notes: { ...current.scope1Notes, ...newData.scope1Notes } }
        );
      }

      // Optimistically update scope2 notes
      if (previous && newData.scope2Notes) {
        const current = queryClient.getQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? '')
        ) ?? previous;
        queryClient.setQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? ''),
          { ...current, scope2Notes: { ...current.scope2Notes, ...newData.scope2Notes } }
        );
      }

      // Optimistically update cache
      if (previous && newData.scope3Categories) {
        const updatedCategories = { ...previous.scope3Categories };
        const updates = newData.scope3Categories;

        for (const categoryKey of Object.keys(updates) as Scope3CategoryKey[]) {
          const value = updates[categoryKey];
          const currentConfig = updatedCategories[categoryKey];
          if (value !== undefined) {
            updatedCategories[categoryKey] = {
              enabled: value.enabled ?? currentConfig.enabled,
              calculationMethod: value.calculationMethod ?? currentConfig.calculationMethod,
            };
          }
        }

        queryClient.setQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? ''),
          {
            ...previous,
            scope3Categories: updatedCategories,
          }
        );
      }

      if (previous && newData.financialImpactAssumptions) {
        queryClient.setQueryData<GHGSettingsResponse>(
          ghgSettingsKeys.detail(organizationId ?? ''),
          {
            ...previous,
            financialImpactAssumptions: {
              ...previous.financialImpactAssumptions,
              ...newData.financialImpactAssumptions,
            },
          }
        );
      }

      return { previous };
    },
    onError: (_err, _newData, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(ghgSettingsKeys.detail(organizationId ?? ''), context.previous);
      }
    },
    onSettled: () => {
      // Refetch after mutation
      void queryClient.invalidateQueries({
        queryKey: ghgSettingsKeys.all,
      });
    },
  });
}

/**
 * Helper hook to update a single Scope 3 category.
 *
 * @example
 * const { updateCategory, isPending } = useUpdateScope3Category();
 * updateCategory('businessTravel', { enabled: false });
 */
export function useUpdateScope3Category() {
  const mutation = useUpdateGHGSettings();

  const updateCategory = (categoryKey: Scope3CategoryKey, config: UpdateScope3CategoryConfig) => {
    mutation.mutate({
      scope3Categories: {
        [categoryKey]: config,
      },
    });
  };

  return {
    updateCategory,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}

/**
 * Query keys for GHG setup wizard state
 */
export const ghgWizardStateKeys = {
  all: ['ghg-wizard-state'] as const,
  detail: (orgId: string) => [...ghgWizardStateKeys.all, 'detail', orgId] as const,
};

/**
 * Hook to fetch persisted GHG setup wizard state from the backend.
 */
export function useGHGSetupWizardState() {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  return useQuery({
    queryKey: ghgWizardStateKeys.detail(organizationId ?? ''),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return fetchSetupWizardState(getToken, organizationId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to persist GHG setup wizard state to the backend.
 */
export function useSaveGHGSetupWizardState() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const organizationId = useGHGOrganizationId();

  return useMutation({
    mutationFn: async (state: Record<string, unknown>) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return saveSetupWizardState(getToken, organizationId, state);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ghgWizardStateKeys.all,
      });
    },
  });
}
